import React, { useCallback, useEffect, useReducer } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import {
  getLevelUpTargetDatesAndProgressStatuses,
  updateCompetencyProgressStatus,
  updateCompetencyTargetDate,
} from 'services/growth/oneOnOne';

import { LevelUpSidePanelContext, LevelUpSidePanelInitialState } from '.';
import { useCompetencySidePanelData } from '../hooks/dataHooks';
import { hideCompetencySidePanelService, showCompetencySidePanelService } from '../services';
import { LEVELUP_SIDEPANEL_ACTIONS } from './actions';
import levelUpSidePanelContextReducer from './reducer';

// todo: Move it to redux cause now competency side-panel will be used platform wide

export const LevelUpSidePanelContextProvider = ({ reportee, children }) => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);

  const history = useHistory();

  const queryClient = useQueryClient();

  // const { run, data, isLoading, isIdle, isError } = useAsync();

  const [reload, forceReload] = useForceUpdate();
  const [reloadOnCommentEdit, forceReloadOnCommentEdit] = useForceUpdate();

  const [levelUpSidePanelState, levelUpSidePanelDispatch] = useReducer(
    levelUpSidePanelContextReducer,
    LevelUpSidePanelInitialState
  );

  const { reporteeId, levelUpId, competencyId, levelUpCompetencyId } = levelUpSidePanelState;

  const openLevelUpSidePanel = useCallback(
    ({
      reporteeId,
      competencyId,
      levelUpId,
      levelUpCompetencyId,
      isLoggedInEmployeeManager, // for action items
      checkInInstanceId,
      checkInParentId,
      filterEmpIds,
      showActionItems = true,
      scrollToId,
      isOneOnOne = false,
    }) => {
      if (levelUpCompetencyId || (competencyId && levelUpId)) {
        showCompetencySidePanelService({
          history,
          employeeId: reporteeId,
          competencyId,
          levelUpCompetencyId,
          levelUpId,
          scrollToId,
          isOneOnOne,
          checkInInstanceId,
          // checkInParentId
        });
        levelUpSidePanelDispatch({
          type: LEVELUP_SIDEPANEL_ACTIONS.OPEN_SIDEPANEL,
          payload: {
            reporteeId,
            competencyId: String(competencyId),
            levelUpId: String(levelUpId),
            levelUpCompetencyId,
            isLoggedInEmployeeManager,
            checkInInstanceId,
            checkInParentId,
            filterEmpIds,
            showActionItems,
          },
        });
      }
    },
    []
  );

  const closeLevelUpSidePanel = useCallback(() => {
    hideCompetencySidePanelService({
      history,
    });
    levelUpSidePanelDispatch({
      type: LEVELUP_SIDEPANEL_ACTIONS.CLOSE_SIDEPANEL,
    });
  }, []);

  const setLevelUpTargetDates = useCallback((targetDates) => {
    levelUpSidePanelDispatch({
      type: LEVELUP_SIDEPANEL_ACTIONS.SET_LEVEL_UP_TARGET_DATES,
      payload: targetDates,
    });
  }, []);

  const setLevelUpProgressStatuses = useCallback((progressStatuses) => {
    levelUpSidePanelDispatch({
      type: LEVELUP_SIDEPANEL_ACTIONS.SET_LEVEL_UP_PROGRESS_STATUSES,
      payload: progressStatuses,
    });
  }, []);

  const setLevelUpCompetencyDetails = useCallback((levelUpCompetencyDetails) => {
    levelUpSidePanelDispatch({
      type: LEVELUP_SIDEPANEL_ACTIONS.SET_LEVEL_UP_COMPETENCY_DETAILS,
      payload: levelUpCompetencyDetails,
    });
  }, []);

  const handleCompetencyTargetDateChange = useCallback(
    async ({ jobFunctionId, jobRoleId, competencyId, subCompetencyId, targetPeriodStart }) => {
      try {
        const res = await updateCompetencyTargetDate({
          employeeId: reporteeId,
          jobFunctionId,
          jobRoleId,
          competencyId,
          subCompetencyId,
          targetPeriodStart,
        });

        queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.CompetencySidePanel] });
        queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.LevelUpCompetencyData] });
      } catch (error) {
        console.error('Error updating target date for competency : ', error);
      }
    },
    [reporteeId]
  );

  const handleCompetencyProgressStatusChange = useCallback(
    ({ jobFunctionId, jobRoleId, competencyId, subCompetencyId, progress, comment }) =>
      updateCompetencyProgressStatus({
        employeeId: reporteeId,
        jobFunctionId,
        jobRoleId,
        competencyId,
        subCompetencyId,
        progress,
        comment,
      })
        .then((res) => {
          levelUpSidePanelDispatch({
            type: LEVELUP_SIDEPANEL_ACTIONS.UPDATE_COMPETENCY_PROGRESS_STATUS,
            payload: progress,
          });
          // using force reload to reload competency comments
          forceReload();
        })
        .catch((err) => console.error(err.message || 'failed to update competency progress status')),
    [reporteeId]
  );

  useEffect(() => {
    // get Level up target dates and Progress Statuses, this is not employee specific
    getLevelUpTargetDatesAndProgressStatuses({ employeeId: loggedInEmployeeId })
      .then((res) => {
        const { targetDateList, progressList } = res;
        setLevelUpTargetDates(targetDateList);
        setLevelUpProgressStatuses(progressList);
      })
      .catch((err) => console.error(err.message || 'failed to fetch level up details'));
  }, []);

  const { data: levelUpCompetencyDetails, isLoading } = useCompetencySidePanelData({
    employeeId: reporteeId,
    levelUpCompetencyId,
    levelUpId,
    competencyId,
  });

  // useEffect(() => {
  //   if (!reporteeId) return;

  //   if (levelUpCompetencyId) {
  //     run(
  //       getLevelUpCompetencyDetails({
  //         employeeId: reporteeId,
  //         levelUpCompetencyId
  //       })
  //         .then(res => {
  //           setLevelUpCompetencyDetails(res);
  //         })
  //         .catch(err => {
  //           console.error(err.message || 'failed to fetch competency details');
  //         })
  //     );
  //     return;
  //   }

  //   if (levelUpId && competencyId) {
  //     run(
  //       getLevelUpCompetencyDetails({
  //         employeeId: reporteeId,
  //         levelUpId,
  //         competencyId
  //       })
  //         .then(res => {
  //           setLevelUpCompetencyDetails(res);
  //         })
  //         .catch(err => {
  //           console.error(err.message || 'failed to fetch competency details');
  //         })
  //     );
  //     return;
  //   }
  // }, [reporteeId, levelUpId, competencyId, levelUpCompetencyId, reload]);

  // useEffect(() => {
  //   if (!reporteeId) return;

  //   if (levelUpCompetencyId) {
  //     getLevelUpCompetencyDetails({
  //       employeeId: reporteeId,
  //       levelUpCompetencyId
  //     })
  //       .then(res => {
  //         setLevelUpCompetencyDetails(res);
  //       })
  //       .catch(err => {
  //         console.error(err.message || 'failed to fetch competency details');
  //       });

  //     return;
  //   }

  //   if (levelUpId && competencyId) {
  //     getLevelUpCompetencyDetails({
  //       employeeId: reporteeId,
  //       levelUpId,
  //       competencyId
  //     })
  //       .then(res => {
  //         setLevelUpCompetencyDetails(res);
  //       })
  //       .catch(err => {
  //         console.error(err.message || 'failed to fetch competency details');
  //       });

  //     return;
  //   }
  // }, [reloadOnCommentEdit]);

  return (
    <LevelUpSidePanelContext.Provider
      value={{
        state: {
          ...levelUpSidePanelState,
          reportee,
          isLoading,
          levelUpCompetencyDetails,
        },
        openLevelUpSidePanel,
        closeLevelUpSidePanel,
        handleCompetencyProgressStatusChange,
        handleCompetencyTargetDateChange,
        forceReloadComments: reload,
        // forceReloadOnCommentEdit
      }}
    >
      {children}
    </LevelUpSidePanelContext.Provider>
  );
};
